/**
 * Compliance → Breach dispatch - Phase 8 batch B item 13 (2026-05-16).
 *
 * The DPO-facing dispatch flow for breach_incidents. Draft incidents
 * are generated by the enforcement pipeline (breach-pre-fill.ts); this
 * page is where the DPO acknowledges, files with the supervisor (DPB /
 * ICO / RBI / SEC / etc.), notifies data principals, and closes.
 *
 * The notifications_rendered field carries pre-rendered regulator-ready
 * markdown - operators copy/paste into the regulator portal and record
 * the receipt via channelEvidence.
 */
import { useCallback, useEffect, useState } from 'react';
import { motion } from 'motion/react';
import {
  RefreshCw,
  Loader2,
  AlertTriangle,
  CheckCircle2,
  Clock,
  XCircle,
  Eye,
  ShieldCheck,
  Send,
  Users,
  CheckSquare,
  FileText,
  Clipboard,
  Check,
} from 'lucide-react';
import { GlassCard, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/primitives';
import { PageContainer, PageHeader } from '@/components/viz/PageHeader';
import { ErrorState, LoadingState } from '@foctta/ui-shell';
import { KpiCard } from '@/components/viz/KpiCard';
import { ClockChips } from '@/pages/detection/Incidents';
import {
  listBreachIncidents,
  acknowledgeBreach,
  notifyBreachDpb,
  notifyBreachDataPrincipals,
  closeBreach,
  type BreachIncident,
  type BreachStatus,
} from '@/lib/api';

// BUG-3 fix (2026-05-26): map is keyed by the canonical Compliance
// workflow lifecycle (draft → acknowledged → notified_dpb →
// notified_data_principals → closed | false_positive). The breach
// schema also accepts the Core-mirror lifecycle statuses ('detected',
// 'assessed', 'contained', 'authority_notified', 'individuals_notified')
// for cross-platform mirror rows; map those to the closest workflow
// state so the page never renders <undefined /> (which threw React
// error #130 the moment a non-workflow status appeared in the table).
const STATUS_VARIANT: Record<string, 'success' | 'warning' | 'critical' | 'glass'> = {
  draft: 'critical',
  acknowledged: 'warning',
  notified_dpb: 'warning',
  notified_data_principals: 'warning',
  closed: 'success',
  false_positive: 'glass',
  // Core-mirror lifecycle (foctta-core-client/types.ts:BreachStatus)
  detected: 'critical',
  assessed: 'critical',
  contained: 'warning',
  authority_notified: 'warning',
  individuals_notified: 'warning',
};

const STATUS_ICON: Record<string, typeof CheckCircle2> = {
  draft: AlertTriangle,
  acknowledged: ShieldCheck,
  notified_dpb: Send,
  notified_data_principals: Users,
  closed: CheckCircle2,
  false_positive: XCircle,
  // Core-mirror lifecycle
  detected: AlertTriangle,
  assessed: AlertTriangle,
  contained: ShieldCheck,
  authority_notified: Send,
  individuals_notified: Users,
};

// Defensive fallback so any unknown status (e.g. a newer state shipped
// by the server before the client catches up) doesn't crash the page.
function statusVariant(s: string): 'success' | 'warning' | 'critical' | 'glass' {
  return STATUS_VARIANT[s] ?? 'glass';
}
function statusIcon(s: string): typeof CheckCircle2 {
  return STATUS_ICON[s] ?? AlertTriangle;
}

const SEVERITY_VARIANT: Record<string, 'critical' | 'warning' | 'glass'> = {
  critical: 'critical',
  high: 'warning',
  medium: 'glass',
  low: 'glass',
};

export function ComplianceBreaches(): JSX.Element {
  const [incidents, setIncidents] = useState<BreachIncident[]>([]);
  const [counts, setCounts] = useState({
    draft: 0, acknowledged: 0, notified_dpb: 0, notified_data_principals: 0,
    closed: 0, false_positive: 0,
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [detail, setDetail] = useState<BreachIncident | null>(null);
  const [selectedRegulator, setSelectedRegulator] = useState<string>('');
  const [copiedKey, setCopiedKey] = useState<string | null>(null);

  const refresh = useCallback(async (): Promise<void> => {
    setLoading(true);
    setError(null);
    try {
      const r = await listBreachIncidents({});
      setIncidents(r.incidents);
      setCounts(r.byStatus);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'failed to load incidents');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { void refresh(); }, [refresh]);

  function openDetail(inc: BreachIncident) {
    setDetail(inc);
    const firstReg = Object.keys(inc.notificationsRendered)[0] ?? '';
    setSelectedRegulator(firstReg);
  }

  async function handleAck(inc: BreachIncident): Promise<void> {
    const reason = window.prompt('Acknowledgement note (optional):') ?? undefined;
    try {
      const r = await acknowledgeBreach(inc.id, reason);
      setDetail(r.incident);
      await refresh();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'acknowledge failed');
    }
  }

  async function handleNotifyDpb(inc: BreachIncident): Promise<void> {
    const evidence = window.prompt('Channel evidence (e.g., "filed via DPB portal 14:32 - DPB-2026-..."):');
    if (!evidence) return;
    try {
      const r = await notifyBreachDpb(inc.id, { channelEvidence: evidence });
      setDetail(r.incident);
      await refresh();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'notify-dpb failed');
    }
  }

  async function handleNotifyPrincipals(inc: BreachIncident): Promise<void> {
    const evidence = window.prompt(`Channel evidence for ${inc.affectedPrincipalsCount == null ? 'the (not established) number of' : inc.affectedPrincipalsCount.toLocaleString()} data principals (e.g., "email blast sent 2026-05-16 - SES batch abc"):`);
    if (!evidence) return;
    try {
      const r = await notifyBreachDataPrincipals(inc.id, { channelEvidence: evidence });
      setDetail(r.incident);
      await refresh();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'notify-principals failed');
    }
  }

  async function handleClose(inc: BreachIncident): Promise<void> {
    const remediation = window.prompt('Remediation summary (required):');
    if (!remediation) return;
    const review = window.prompt('Post-incident review summary (required):');
    if (!review) return;
    try {
      const r = await closeBreach(inc.id, {
        remediationSummary: remediation,
        postIncidentReviewSummary: review,
      });
      setDetail(r.incident);
      await refresh();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'close failed');
    }
  }

  async function copy(key: string, value: string): Promise<void> {
    try {
      await navigator.clipboard.writeText(value);
      setCopiedKey(key);
      setTimeout(() => setCopiedKey(null), 1500);
    } catch { /* no-op */ }
  }

  return (
    <PageContainer>
      <PageHeader
        title="Breach dispatch (GDPR Art 33-34 / DPDPA §8(7))"
        subtitle="The DPO-facing dispatch flow for incidents the enforcement pipeline drafts. Acknowledge → notify supervisor → notify data principals → close."
        badges={[
          { label: 'GDPR Art 33', variant: 'glass' },
          { label: 'DPDPA §8(7)', variant: 'glass' },
          ...(loading
            ? [{ label: 'Loading', variant: 'glass' as const }]
            : error
              ? [{ label: 'Load error', variant: 'warning' as const }]
              : [{ label: 'Live', variant: 'success' as const }]),
        ]}
        actions={
          <Button size="sm" variant="glass" onClick={() => void refresh()} disabled={loading}>
            <RefreshCw className={`h-3.5 w-3.5 mr-1 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        }
      />

      {error && (
        <ErrorState
          title="Breach action failed"
          detail={error}
          onRetry={() => setError(null)}
          retryLabel="Dismiss"
        />
      )}

      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-6">
        <KpiCard label="Draft" value={counts.draft} icon={AlertTriangle} glow={counts.draft > 0 ? 'critical' : 'low'} hint="needs DPO ack" />
        <KpiCard label="Acknowledged" value={counts.acknowledged} icon={ShieldCheck} glow={counts.acknowledged > 0 ? 'medium' : 'low'} hint="open w/ DPO" />
        <KpiCard label="Notified DPB" value={counts.notified_dpb} icon={Send} glow={counts.notified_dpb > 0 ? 'medium' : 'low'} hint="supervisor filed" />
        <KpiCard label="Notified principals" value={counts.notified_data_principals} icon={Users} glow={counts.notified_data_principals > 0 ? 'medium' : 'low'} hint="subjects informed" />
        <KpiCard label="Closed" value={counts.closed} icon={CheckCircle2} glow={counts.closed > 0 ? 'high' : 'low'} hint="post-RCA" />
      </div>

      <GlassCard className="p-5">
        <CardHeader className="p-0 mb-3">
          <CardTitle>Breach incidents</CardTitle>
          <CardDescription clamp>
            Click a row to open the dispatch flow. The notifications panel carries pre-rendered
            DPDPA / GDPR / RBI / HIPAA / etc. drafts ready to file with the supervisor.
          </CardDescription>
        </CardHeader>

        {loading && (
          <LoadingState caption="Loading incidents…" />
        )}

        {!loading && incidents.length === 0 && (
          <div className="glass-lite rounded p-8 text-center text-sm text-muted-foreground">
            <CheckCircle2 className="h-8 w-8 mx-auto mb-3 text-success" />
            <div className="font-medium text-foreground/90">No breach incidents on file</div>
            <div className="mt-1">
              The enforcement pipeline drafts incidents when triggered. None right now.
            </div>
          </div>
        )}

        {!loading && incidents.length > 0 && (
          <div className="overflow-x-auto">
            <table className="w-full text-xs tabular">
              <thead>
                <tr className="text-left text-muted-foreground border-b border-border/30">
                  <th className="py-2 pr-3 font-normal">Status</th>
                  <th className="py-2 pr-3 font-normal">Severity</th>
                  <th className="py-2 pr-3 font-normal">Request</th>
                  <th className="py-2 pr-3 font-normal">Window</th>
                  <th className="py-2 pr-3 font-normal whitespace-nowrap">Records</th>
                  <th className="py-2 pr-3 font-normal whitespace-nowrap">Principals</th>
                  <th className="py-2 pr-3 font-normal">Categories</th>
                  <th className="py-2 pr-3 font-normal">Regulator clocks</th>
                  <th className="py-2 pr-3 font-normal" />
                </tr>
              </thead>
              <tbody>
                {incidents.map((inc, i) => {
                  const SIcon = statusIcon(inc.status);
                  return (
                    <motion.tr
                      key={inc.id}
                      initial={{ opacity: 0, x: -4 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: Math.min(i * 0.02, 0.3) }}
                      className="border-b border-border/15 hover:bg-muted/20 cursor-pointer align-top"
                      onClick={() => openDetail(inc)}
                    >
                      <td className="py-2 pr-3 whitespace-nowrap">
                        <Badge variant={statusVariant(inc.status)} className="text-[10px] inline-flex items-center gap-1">
                          <SIcon className="h-3 w-3" />
                          {inc.status}
                        </Badge>
                      </td>
                      <td className="py-2 pr-3 whitespace-nowrap">
                        <Badge variant={SEVERITY_VARIANT[inc.recommendedSeverity] ?? 'glass'} className="text-[10px]">
                          {inc.recommendedSeverity}
                        </Badge>
                      </td>
                      <td className="py-2 pr-3 font-mono text-[10px] truncate max-w-[18ch]" title={inc.requestId}>
                        {inc.requestId}
                      </td>
                      <td className="py-2 pr-3 whitespace-nowrap text-[10px] text-muted-foreground">
                        {inc.breachWindowStart
                          ? new Date(inc.breachWindowStart).toLocaleDateString()
                          : '-'}
                      </td>
                      {/* null is "not established" (migration 0138), never 0. */}
                      <td className="py-2 pr-3 tabular">{inc.affectedRecordCount == null ? 'Not established' : inc.affectedRecordCount.toLocaleString('en-IN')}</td>
                      <td className="py-2 pr-3 tabular">{inc.affectedPrincipalsCount == null ? 'Not established' : inc.affectedPrincipalsCount.toLocaleString('en-IN')}</td>
                      <td className="py-2 pr-3 max-w-[20ch] truncate" title={inc.dataCategories.join(', ')}>
                        {inc.dataCategories.join(', ')}
                      </td>
                      <td className="py-2 pr-3 min-w-[16ch]">
                        {/* Q34: started by "Confirm as breach" on a DDR incident. */}
                        {inc.regulatoryClocks && inc.regulatoryClocks.length > 0 ? <ClockChips clocks={inc.regulatoryClocks} /> : <span className="text-muted-foreground">-</span>}
                      </td>
                      <td className="py-2 pr-3">
                        <Eye className="h-3 w-3 text-muted-foreground" />
                      </td>
                    </motion.tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </GlassCard>

      {/* Detail / dispatch modal */}
      <Dialog open={detail !== null} onOpenChange={(o) => { if (!o) setDetail(null); }}>
        <DialogContent className="max-w-5xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-4 w-4 text-warning" />
              Breach incident - dispatch
            </DialogTitle>
          </DialogHeader>
          {detail && (
            <div className="space-y-3 text-xs">
              {/* Header chips */}
              <div className="flex items-center gap-2 flex-wrap">
                <Badge variant={statusVariant(detail.status)} className="text-[10px]">
                  {detail.status}
                </Badge>
                <Badge variant={SEVERITY_VARIANT[detail.recommendedSeverity] ?? 'glass'} className="text-[10px]">
                  severity: {detail.recommendedSeverity}
                </Badge>
                <code className="font-mono text-[10px] text-muted-foreground">
                  {detail.requestId}
                </code>
              </div>
              {detail.regulatoryClocks && detail.regulatoryClocks.length > 0 && (
                <div className="glass-lite rounded p-3 space-y-1">
                  <div className="text-muted-foreground font-medium">
                    Regulator clocks, started when it was confirmed as a breach{detail.confirmedAt ? ` (${new Date(detail.confirmedAt).toLocaleString('en-IN')})` : ''}
                  </div>
                  <ClockChips clocks={detail.regulatoryClocks} />
                  {detail.corePushStatus === 'not_sent' && <div className="text-muted-foreground">Not yet sent to FOCTTA Core.</div>}
                </div>
              )}

              {/* State machine action bar */}
              <div className="glass-lite rounded p-3">
                <div className="text-muted-foreground mb-2 font-medium">Workflow actions</div>
                <div className="flex flex-wrap gap-2">
                  {detail.status === 'draft' && (
                    <Button size="sm" data-action="disc.comp_breaches.a01" onClick={() => void handleAck(detail)}>
                      <ShieldCheck className="h-3.5 w-3.5 mr-1" />
                      Acknowledge
                    </Button>
                  )}
                  {(detail.status === 'acknowledged' || detail.status === 'notified_data_principals') && detail.notifiedDpbAt === null && (
                    <Button size="sm" data-action="disc.comp_breaches.a02" onClick={() => void handleNotifyDpb(detail)}>
                      <Send className="h-3.5 w-3.5 mr-1" />
                      Mark notified to supervisor
                    </Button>
                  )}
                  {(detail.status === 'acknowledged' || detail.status === 'notified_dpb') && detail.notifiedDataPrincipalsAt === null && (
                    <Button size="sm" data-action="disc.comp_breaches.a03" onClick={() => void handleNotifyPrincipals(detail)}>
                      <Users className="h-3.5 w-3.5 mr-1" />
                      Mark notified to data principals
                    </Button>
                  )}
                  {detail.status !== 'closed' && detail.status !== 'draft' && (
                    <Button size="sm" variant="glass" data-action="disc.comp_breaches.a04" onClick={() => void handleClose(detail)}>
                      <CheckSquare className="h-3.5 w-3.5 mr-1" />
                      Close (post-incident review)
                    </Button>
                  )}
                  {detail.status === 'closed' && (
                    <Badge variant="success" className="text-[10px]">
                      Workflow complete
                    </Badge>
                  )}
                </div>
              </div>

              {/* Pre-rendered notifications */}
              <div>
                <div className="font-medium mb-1">Regulator-ready notifications</div>
                <div className="text-[10px] text-muted-foreground mb-2">
                  Copy / paste into the supervisor portal; record the receipt via the
                  &quot;mark notified&quot; action above.
                </div>
                <div className="flex flex-wrap gap-1 mb-2">
                  {Object.keys(detail.notificationsRendered).map((reg) => (
                    <button
                      key={reg}
                      type="button"
                      onClick={() => setSelectedRegulator(reg)}
                      className={`px-2 py-0.5 rounded text-[10px] font-mono transition-colors ${
                        selectedRegulator === reg
                          ? 'bg-primary text-primary-foreground'
                          : 'glass-lite hover:bg-muted/30'
                      }`}
                    >
                      {reg}
                    </button>
                  ))}
                </div>
                {selectedRegulator && (
                  <div className="relative">
                    <Button
                      size="sm"
                      variant="glass"
                      className="absolute top-2 right-2 z-10"
                      onClick={() => void copy(selectedRegulator, detail.notificationsRendered[selectedRegulator] ?? '')}
                    >
                      {copiedKey === selectedRegulator
                        ? <Check className="h-3 w-3" />
                        : <Clipboard className="h-3 w-3" />}
                    </Button>
                    <pre className="glass-lite rounded p-3 text-[10px] font-mono whitespace-pre-wrap max-h-80 overflow-y-auto">
                      {detail.notificationsRendered[selectedRegulator]}
                    </pre>
                  </div>
                )}
              </div>

              {/* Timeline + facts */}
              <div className="grid grid-cols-2 gap-2">
                <div className="glass-lite rounded p-2">
                  <div className="text-muted-foreground mb-0.5">Breach window</div>
                  <code className="font-mono text-[10px]">
                    {detail.breachWindowStart ? new Date(detail.breachWindowStart).toLocaleString() : '-'}
                  </code>
                  <div className="text-[10px] text-muted-foreground">→</div>
                  <code className="font-mono text-[10px]">
                    {detail.breachWindowEnd ? new Date(detail.breachWindowEnd).toLocaleString() : '-'}
                  </code>
                </div>
                <div className="glass-lite rounded p-2">
                  <div className="text-muted-foreground mb-0.5">Scope</div>
                  <div className="tabular">
                    {detail.affectedRecordCount == null ? 'Records not established' : `${detail.affectedRecordCount.toLocaleString('en-IN')} records`} /{' '}
                    {detail.affectedPrincipalsCount == null ? 'principals not established' : `${detail.affectedPrincipalsCount.toLocaleString('en-IN')} principals`}
                  </div>
                </div>
                {detail.acknowledgedAt && (
                  <div className="glass-lite rounded p-2 col-span-2">
                    <div className="text-muted-foreground mb-0.5">Acknowledged</div>
                    <code className="font-mono text-[10px]">
                      {new Date(detail.acknowledgedAt).toLocaleString()}
                      {detail.acknowledgedByUserId && <> by {detail.acknowledgedByUserId.slice(0, 8)}…</>}
                    </code>
                  </div>
                )}
                {detail.notifiedDpbAt && (
                  <div className="glass-lite rounded p-2">
                    <div className="text-muted-foreground mb-0.5">Supervisor notified</div>
                    <code className="font-mono text-[10px]">{new Date(detail.notifiedDpbAt).toLocaleString()}</code>
                  </div>
                )}
                {detail.notifiedDataPrincipalsAt && (
                  <div className="glass-lite rounded p-2">
                    <div className="text-muted-foreground mb-0.5">Data principals notified</div>
                    <code className="font-mono text-[10px]">{new Date(detail.notifiedDataPrincipalsAt).toLocaleString()}</code>
                  </div>
                )}
                {detail.closedAt && (
                  <div className="glass-lite rounded p-2 col-span-2">
                    <div className="text-muted-foreground mb-0.5">Closed</div>
                    <code className="font-mono text-[10px]">{new Date(detail.closedAt).toLocaleString()}</code>
                    {detail.remediationSummary && (
                      <div className="mt-1">
                        <div className="text-muted-foreground">Remediation:</div>
                        <div>{detail.remediationSummary}</div>
                      </div>
                    )}
                    {detail.postIncidentReviewSummary && (
                      <div className="mt-1">
                        <div className="text-muted-foreground">Post-incident review:</div>
                        <div>{detail.postIncidentReviewSummary}</div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </PageContainer>
  );
}
